import React from "react";
import LmsFormGenerator, { BCFormController, IFormGeneratorFieldOptions } from "../ui/LmsFormGenerator";
import LmsTabPages from "../ui/LmsTabPages";
import { useFetchData, useParseRouteActions, appendParam, appendParams, isEmptyValue, removeProp } from "../../../helpers/GlobalHelper";
import LmsTable from "../ui/LmsTable";
import {
  AddInsuranceForm,
  AddCommentForm,
  addMortgageTableFormFields,
  addAssetsLiabilityFields,
  AddStateTaxForm,
  EditStateTaxForm,
  addContactFormFields,
  EditInsuranceForm,
  ViewCommentForm,
  handleOnScheduleReoBlur
} from "../ui/LmsSharedForms";
import {
  InsuranceTable,
  StateTaxTable,
  BanksTable,
  CommentsTable,
  AssetsLiabilitiesTable,
  ScheduleOfReoTable,
  ExistingContactTable
} from "../ui/LmsSharedTables";
import { IDefaultPagesProps } from "../../interfaces/Lms";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { AddContactForm } from "./LmsOrganizationsContacts";

export const ViewAdditionalBorrowerInfoForm = ({ id = "", orgId = "", isSpe = false, borrowerOrganizationId = "", handleClose = undefined as any }) => {
  const additonalInfoSuffix = isSpe || borrowerOrganizationId ? `orgs/spe-additional-guarantor-info/${orgId}` : `loandetails/entities/${id}/additional-guarantor-info`;
  const [{ data, isLoading, error, fetchData, setLoading }, callApiNow] = useFetchData(additonalInfoSuffix);
  const agiSuffix = data?.assetOrganizationContactId ? `orgs/additional-guarantor-info?id=${data?.id}&assetContactId=${data?.assetOrganizationContactId}` : "";
  const [{ data: adgInfoData, isLoading: isAgiLoading }] = useFetchData(agiSuffix ? agiSuffix : "");
  const defaultData = { totalLiabilities: 0, netWorth: 0 };
  const myForm = new BCFormController({ ...defaultData, ...data, ...adgInfoData }, { submitSuffix: LmsEndpoints.Orgs.PUT.ADDITIONAL_GUARANTOR_INFO });

  let orgField: any;
  if (isSpe) {
    orgField = myForm.getDropdownField("Special Purpose Entity", `orgs/list-spes/${borrowerOrganizationId}`, { disabled: true, name: "specialPurposeEntityId" });
  }
  else {
    orgField = myForm.getDropdownField("Organization", "metadata/list-borrower-organizations", { disabled: true, name: "organizationId" });
  }

  myForm.addGroupFields([
    orgField,
    myForm.getCheckboxField("Use Contact Assets/Real Estate?", { name: "useContactData" }),
    myForm.getDropdownField("Contact", "orgs/list-organization-contacts", {
      name: "assetOrganizationContactId",
      isDisabledOn: { useContactData: true },
      isDisabledOnOperation: "notequal",
      required: data?.useContactData
    }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getInputField("EIN/TIN", { name: "employeeIdentificationNumber", validations: ["EIN/TIN", "required"] }),
    myForm.getCurrencyField("Net Income (Two Year Average)", { required: true, name: "monthlyIncome" }),
    myForm.getCurrencyField("Gross Revenue (Two Year Average)", { name: "monthlyRevenue", required: true }),
    myForm.getNumberField("Years In Business", { required: true }),
  ]);
  myForm.addGroupFields([
    myForm.getCheckboxField("No Real Estate Owned", { name: "noRealEstate", width: 2 }),
    myForm.getCheckboxField("No Assets/Liabilities", { name: "noAsset", width: 2 }),
    myForm.getCurrencyField("Value Of Real Estate Owned", {
      actionPath: appendParam(`/organizations-borrowers/viewscheduleofreo/${orgId}`, "isSpe", isSpe),
      actionLabel: "View Schedule Of REO",
      disabled: true,
      isHidden: { noRealEstate: true },
      width: 4
    }),
    myForm.getCurrencyField("Value Of Real Estate Owned", { isHidden: { noRealEstate: true }, isHiddenOperation: "notequal", width: 4, disabled: true }),
    myForm.getCurrencyField("Value Of Stocks And Bonds", {
      actionPath: appendParam(`/organizations-borrowers/viewassetsliabilities/${orgId}`, "borrowerOrganizationId", borrowerOrganizationId),
      actionLabel: "View Assets/Liabilities",
      disabled: true,
      isHidden: { noAsset: true },
      width: 4
    }),
    myForm.getCurrencyField("Value Of Stocks And Bonds", { isHidden: { noAsset: true }, isHiddenOperation: "notequal", width: 4, disabled: true }),
    myForm.getNumberField("# Of Properties Owned", { name: "numberOfPropertiesOwned", disabled: true, width: 4 }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getCurrencyField("Cash In Bank", { disabled: true }),
    myForm.getCurrencyField("Total Liabilities", { disabled: true }),
    myForm.getCurrencyField("Net Worth", { disabled: true }),
    myForm.getInputField("Estimated Credit Score", { fieldType: "number" }),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("Any Pending or Threatened Legal Actions?", { position: "right-center", width: 4 }),
    myForm.getCheckboxField("", { name: "pendingLegalAction" }),
    myForm.getInputField("", { name: "legalActionDescription", width: 8 }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getLabelField("Foreclosure Ever Initiated Against You?", { position: "right-center", width: 4 }),
    myForm.getCheckboxField("", { name: "isForeclosureInitiated" }),
    myForm.getInputField("", { name: "foreclosureDescription", width: 8 }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getLabelField("Other Names Under Which You Have Been Granted Credit", { position: "right-center", width: 4 }),
    myForm.getInputField("", { name: "otherCreditName", width: 4 }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getLabelField("Comments", { position: "right-center", width: 4 }),
    myForm.getTextareaField("", { name: "comment", width: 8 }),
  ], { widths: 1 });

  const onFormChanged = async (options, lastUpdated) => {
    const assetOrganizationContactId = options?.inputs?.assetOrganizationContactId || "";
    if (lastUpdated === "assetOrganizationContactId" && assetOrganizationContactId) {
      const params = [{ id: data?.id, assetContactId: assetOrganizationContactId }] as any[];
      setLoading(true);
      const newDetails = await fetchData(appendParams("orgs/additional-guarantor-info", params));
      if (newDetails?.data) {
        let updatedRealEstateAsset = {};
        updatedRealEstateAsset = {
          noRealEstate: newDetails?.data?.numberOfPropertiesOwned === 0,
          noAsset: newDetails?.data?.valueOfStocksAndBonds === 0 && newDetails?.data?.cashInBank === 0
        };
        options.setInputs({ ...options?.inputs, ...updatedRealEstateAsset, ...newDetails?.data });
        if (lastUpdated === "assetOrganizationContactId" && assetOrganizationContactId) {
          options.setDisabledFields({ noRealEstate: true, noAsset: true });
        }
        setLoading(false);
      }
      else {
        setLoading(false);
      }
    }

    if (lastUpdated === "useContactData") {
      if (!options?.inputs?.useContactData) {
        options.setInputs(removeProp(options?.inputs, "assetOrganizationContactId"));
        options.setValidations(removeProp(options?.validations, "assetOrganizationContactId"));
      }
      else {
        options.setValidations({ ...options?.validations, assetOrganizationContactId: ["required"] });
      }
    }
  };

  const refreshButton = { text: "Refresh", handleClick: callApiNow };
  const formActions = ["save", "save-close", refreshButton, "close"];
  return <LmsFormGenerator 
    myForm={myForm} 
    isLoading={isLoading || isAgiLoading} 
    error={error} 
    actions={formActions} 
    onFormChanged={onFormChanged}
    handleClose={handleClose}
  />;
};

const addBorrowerOrganizationFields = (myForm, orgId, isEdit = false) => {
  const organizationTypeFieldOptions = { required: true, disabled: true, name: "organizationTypeId", value: GlobalConstants.OrganizationTypes.Borrower };
  myForm.addGroupFields([
    myForm.getDropdownField("Organization Type", "metadata/list-borrower-org-types", organizationTypeFieldOptions),
    myForm.getInputField("Organization Name", { name: "name", required: true }),
    myForm.getInputField("Internal Abbreviation", { name: "abbreviation", validations: ["abbreviation"] }),
    myForm.getDropdownField("Type", "user/metadata", { required: true, dropdownOptionsSubPath: "entitySubTypes", name: "entitySubTypeId", disabled: isEdit }),
    myForm.getCheckboxField("Is Active", { value: true }),
  ]);
  myForm.addAddressSectionToForm();
  myForm.addGroupFields([
    myForm.getInputField("Website", { validations: ["website"] }),
    myForm.getDropdownField("Loan Officer working with", "orgs/list-loan-officer-contacts", { name: "loanOfficerOrganizationContactId" }),
    myForm.getInputField("Business Phone", { validations: ["phone"], fieldType: "phone" }),
    myForm.getInputField("Email", { validations: ["email"] }),
    myForm.getBlankField()
  ]);

  if (isEdit) {
    const contactOptions: IFormGeneratorFieldOptions = {
      name: "loanContactOrganizationContactId",
      onDisabledLabel: "Organization Contact",
      actionLabel: "Add New Contact",
      modalContent: (setIsAddFormModalOpen, options) => <AddContactForm orgId={orgId} setIsModalOpen={setIsAddFormModalOpen as any} dropdownOptions={options as any} />,
      actionIcon: GlobalConstants.Icons.CARET_UP,
      required: true,
      width: 5,
      isActionHiddenOnDisabled: true,
    };

    myForm.addGroupFields([
      myForm.getDropdownField("Default Loan Contact", `orgs/list-loan-org-contacts/${orgId}`, contactOptions),
    ]);
  }

  return myForm;
};

const AddCopyBorrowerOrganization = ({ orgId, isCopy = false }) => {
  const [{ data, error, isLoading }] = useFetchData(isCopy ? `orgs/borrower/${orgId}` : "");
  const initialData = data ? removeProp(data, "id") : {};
  const myForm = addBorrowerOrganizationFields(new BCFormController(initialData, {
    submitSuffix: LmsEndpoints.Orgs.POST.ORG,
    submitMethod: "POST",
    payloadDataType: "FormData",
    saveAndContinueSuffix: "organizations-borrowers"
  }), orgId);


  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} />;
};

const EditBorrowerOrganization = ({ orgId }) => {
  const [{ data, error, isLoading }] = useFetchData(`orgs/borrower/${orgId}`);
  const myForm = addBorrowerOrganizationFields(new BCFormController(data, {
    submitSuffix: LmsEndpoints.Orgs.PUT.ORG,
    payloadDataType: "FormData",
    clearSuffixesOnSubmit: [`orgs/borrower/${orgId}`],
    initIsDisabled: true,
    exitEditOnClose: true
  }), orgId, true);

  const viewAdditionalInfoAction = { text: "View/Edit Additional Information", path: `/organizations-borrowers/viewadditionalinfo/${data?.additionalGuarantorInfoId}?orgId=${orgId}` };
  const topActions = ["edit", viewAdditionalInfoAction];
  const formActions = ["save", "save-close", "edit", "close"];

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={formActions}
      topActions={topActions}
      hideBottomActionsOnEdit
    />
  );
};

const AddSPEContact = ({ setIsModalOpen, options }) => {
  const [{ fetchData }] = useFetchData();
  const orgId = options?.useFormInstance?.inputs?.organizationId || "";
  const [{ data: orgInfodata, isLoading, error }] = useFetchData(`orgs/info/${orgId}`);

  const onSubmitSuccess = (response) => {
    if (FeatureFlags.IS_DEBUG) {
       console.log(response, options?.useFormInstance); // TODO: Add created contact id to SPE Contact field
    }
  };

  const handleFormChange = async ({ inputs, setInputs }, lastUpdated) => {
    if (lastUpdated === "organizationId" && inputs?.organizationId ) {
      const { data } = await fetchData(`orgs/info/${inputs?.organizationId }`);
        setInputs({ ...inputs, ...data});
    }
  };

  const myForm = new BCFormController(orgInfodata, { submitSuffix: LmsEndpoints.Orgs.POST.CONTACT_ORG, onSubmitSuccess });
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { required: true, name: "organizationId", value: orgId })]);
  addContactFormFields(myForm, true, { organizationId: orgId });

  return <LmsFormGenerator myForm={myForm} onFormChanged={handleFormChange} isLoading={isLoading} error={error} actions={["save-new", "save-continue", "save-close", "close"]} handleClose={() => setIsModalOpen(false)}/>;
};

const addSPEBorrowerOrganizationFields = (myForm, orgId = "") => {
  myForm.addGroupFields([
    myForm.getDropdownField("Organization", "orgs/list-orgs", { name: "organizationId", disabled: !!orgId, required: true }),
    myForm.getInputField("SPE Company Name", { name: "name", required: true }),
    myForm.getInputField("Abbreviation", { name: "abbreviation", validations: ["abbreviation"] }),
    myForm.getCheckboxField("Is Active", { value: true }),
    myForm.getBlankField()
  ]);
  myForm.addAddressSectionToForm(false, undefined, true);

  const addSpeContactOptions: IFormGeneratorFieldOptions = {
    name: "organizationContactId",
    dependencyField: "organizationId",
    actionLabel: "Add New Contact",
    modalContent: (setIsAddFormModalOpen, options) => <AddSPEContact setIsModalOpen={setIsAddFormModalOpen} options={options}/>,
    modalSize: "fullscreen",
    actionIcon: GlobalConstants.Icons.CARET_UP,
    actionHasDependency: true,
    isActionHiddenOnDisabled: true,
    width: 8,
  };

  myForm.addGroupFields([
    myForm.getInputField("EIN/TIN", { validations: ["EIN/TIN", "required"] , name: "employerIdentificationNumber", width: 4, charLimit: 9 }),
    myForm.getDropdownField("SPE Contact", "metadata/list-spe-contacts", addSpeContactOptions),
    myForm.getBlankField()
  ], { widths: 2 });

  return myForm;
};

export const AddSPEBorrowerOrganization = ({ orgId, handleClose = undefined as any, onSubmitSuccess = undefined as any, saveAndCloseOnly = false }) => {
  const [{ data: orgInfodata, isLoading, error, fetchData }] = useFetchData(orgId ? `orgs/info/${orgId}` : "");
  const initialData = orgId ? { organizationId: orgId } : {};
  const formData = { ...initialData, ...orgInfodata };
  if (formData.name) {
    formData.name = null;
  }
  
  const myForm = addSPEBorrowerOrganizationFields(new BCFormController(formData, {
    submitSuffix: LmsEndpoints.Orgs.POST.SPE,
    excludeFromSubmit: ["id"],
    submitMethod: "POST",
    onSubmitSuccess,
    clearSuffixesOnSubmit: ["orgs/borrower-spes"]
  }), orgId);

  const actions = saveAndCloseOnly ? ["save-close", "close"] : ["save-new", "save-continue", "save-close", "close"];
  const handleOnFormChanged = async (options, lastUpdated) => {
    if (lastUpdated === "organizationId") {
      const { data } = await fetchData(`orgs/info/${options.inputs.organizationId}`).catch(e => console.error(e));
      const newInputs = { ...options.inputs };
      Object.entries(options.addressFields).map(([addressKey, mailingKey]) => {
        newInputs[addressKey] = typeof data[addressKey] === "object" ? data[addressKey].name : data[addressKey];
        if (typeof mailingKey === "string") {
          newInputs[mailingKey] = data[mailingKey];
        }
      });

      if (data && data?.organizationTypeId) {
        newInputs.organizationTypeId = data?.organizationTypeId;
      }

      options.setInputs(newInputs);
    }
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={actions}
      onFormChanged={handleOnFormChanged}
      handleClose={handleClose}
    />
  );
};

const EditSPEForm = ({ orgId, borrowerOrganizationId }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(orgId ? `orgs/special-purose-entity/${orgId}` : "");
  const myForm = addSPEBorrowerOrganizationFields(new BCFormController(data, {
    submitSuffix: LmsEndpoints.Orgs.PUT.SPE,
    initIsDisabled: true,
    exitEditOnClose: true
  }));

  const viewAdditionalInfoAction = {
    text: "View/Edit Additional Information",
    path: appendParam(`/organizations-borrowers/viewspeadditionalinfo/${orgId}`, "borrowerOrganizationId", borrowerOrganizationId)
  };

  const topActions = ["edit", viewAdditionalInfoAction];
  const formActions = ["save", "save-close", "close"];

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={formActions}
      topActions={topActions}
      hideBottomActionsOnEdit
    />
  );
};

const EditSPEBorrowerOrganization = ({ orgId, borrowerOrganizationId }) => {
  const path = "organizations-borrowers";
  const editReoPath = "organizations-borrowers/editscheduleofreo/{id}?isSpe=true";
  const tabPages = {
    SPE: <EditSPEForm orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />,
    Contacts: <ContactsTable suffix={`orgs/spe-contacts/${orgId}`} orgId={borrowerOrganizationId || orgId} isSpe={!!borrowerOrganizationId} />,
    Comments: <CommentsTable suffix={`orgs/spe-comments/${orgId}`} path={path} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />,
    Banks: <BanksTable suffix={`orgs/spe-bank-info/${orgId}`} />,
    Insurance: <InsuranceTable suffix={`orgs/spe-insurances/${orgId}`} path={path} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />,
    "State Tax": <StateTaxTable suffix={`orgs/spe-state-taxes/${orgId}`} path={path} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />,
    "Schedule of REO": <ScheduleOfReoTable suffix={`orgs/spe-reo-schedules/${orgId}`} path={path} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} editPath={editReoPath} />,
    "Assets/Liabilities": <AssetsLiabilitiesTable suffix={`orgs/spe-al-schedules/${orgId}`} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />,
  };

  return <LmsTabPages tabPages={tabPages}/>;
};

const addScheduleOfReoFields = (myForm, isSpe) => {
  const marketValueValidation = ({ inputs }) => {
    const { marketValue } = inputs;
    if (isEmptyValue(marketValue) || marketValue < 1) {
      return {
        message: "value greater than or equal to 1",
      };
    }

    return false;
  };

  let organizationField;
  if (isSpe) {
    organizationField = myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", { disabled: true, name: "specialPurposeEntityId" });
  }
  else {
    organizationField = myForm.getDropdownField("Borrower", "orgs/list-orgs", { disabled: true, name: "borrowerOrganizationId" });
  }

  myForm.addGroupFields([
    organizationField,
    myForm.getDropdownField("REO Status", "orgs/list-reo-statuses", { name: "reostatusId", required: true }),
    myForm.getDropdownField("REO Type", "orgs/list-reo-types", { name: "reotypeId", required: true }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getCurrencyField("Market Value", { required: true, errorLabel: "Please enter", validations: [{ custom: marketValueValidation }] }),
    myForm.getCurrencyField("Gross Rental Income", { name: "grossRentalIncome", required: true }),
    myForm.getInputField("% Owned", { name: "percentageOwned", fieldType: "percent" }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getZipCodeField(),
    myForm.getStateField(),
    myForm.getCityField(),
    myForm.getStreetField(),
  ]);
  return addMortgageTableFormFields(myForm);
};

const AddScheduleOfReoForm = ({ orgId, isSpe }) => {
  const commonInitialValues = {
    mortgageAmount1: 0,
    mortgageAmount2: 0,
    mortgageAmount3: 0,
    mortgagePayment1: 0,
    mortgagePayment2: 0,
    mortgagePayment3: 0
  };

  const initialValues = isSpe ? { specialPurposeEntityId: orgId } : { borrowerOrganizationId: orgId };
  const myForm = addScheduleOfReoFields(new BCFormController({ ...commonInitialValues, ...initialValues }, {
    skipPlaceHolder: true,
    submitSuffix: LmsEndpoints.Orgs.POST.REO_SCHEDULE,
    saveAndContinueSuffix: appendParam("organizations-borrowers/editscheduleofreo/{id}", "isSpe", isSpe)
  }), isSpe);

  return <LmsFormGenerator myForm={myForm} onBlur={handleOnScheduleReoBlur} />;
};

const EditScheduleOfReoForm = ({ id, isSpe }) => {
  const getOrgReoScheduleSuffix = `orgs/reo-schedule/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(getOrgReoScheduleSuffix);
  const myForm = addScheduleOfReoFields(new BCFormController(data, {
    submitSuffix: "orgs/update-reo-schedule",
    deleteSuffix: "orgs/reo-schedules",
    clearSuffixesOnSubmit: [getOrgReoScheduleSuffix]
  }), isSpe);

  const formActions = ["save", "save-close", "delete", "close"];
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} onBlur={handleOnScheduleReoBlur} />;
};

const addBorrowerDropdownField = (myForm, borrowerOrganizationId) => {
  if (borrowerOrganizationId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", { disabled: true, name: "specialPurposeEntityId" })]);
  }
  else {
    myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "orgs/list-borrower-orgs", { disabled: true, name: "borrowerOrganizationId" })]);
  }
};
const AddAssetLiabilityForm = ({ orgId, borrowerOrganizationId = "" }) => {
  const initialValues = borrowerOrganizationId ? { specialPurposeEntityId : orgId } : { borrowerOrganizationId: orgId };
  const myForm = new BCFormController(initialValues, {
    values: borrowerOrganizationId ? { borrowerOrganizationId } : {},
    skipPlaceHolder: true,
    submitSuffix: LmsEndpoints.Orgs.POST.AL_SCHEDULE
  });

  addBorrowerDropdownField(myForm, borrowerOrganizationId);
  addAssetsLiabilityFields(myForm);
  return <LmsFormGenerator myForm={myForm} />;
};

const EditAssetLiabilityForm = ({ id, borrowerOrganizationId = "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(id ? `orgs/al-schedule/${id}` : "");
  const myForm = new BCFormController(data, {
    values: borrowerOrganizationId ? { borrowerOrganizationId } : {},
    submitSuffix: LmsEndpoints.Orgs.PUT.AL_SCHEDULE
  });

  addBorrowerDropdownField(myForm, borrowerOrganizationId);
  addAssetsLiabilityFields(myForm);
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} />;
};

const SPETable = ({ orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations-borrowers/addspe?orgId=${orgId}` },
    { text: "Edit", path: "/organizations-borrowers/editspe" },
    "remove",
    "export",
    "activate",
    "deactivate"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={`orgs/special-purose-entities/${orgId}`}
    onClickPath="/organizations-borrowers/editspe"
    deleteSuffix="orgs/borrower-spes"
    activateSuffix="orgs/activate-spes"
    deActivateSuffix="orgs/deactivate-spes"
    columnLabelsDictionary={{ name: "SPE Company Name", ...GlobalConstants.ContactsTableColumnLabelsDictionary, ...GlobalConstants.TableColumnLabelsDictionary }}
  />;
};

const ContactsTable = ({ suffix, orgId, isSpe = false }) => {
  const actionsButtons = [
    { text: isSpe ? "Add" : "Add New", action: "add", path: `/organizations-contacts/add?orgId=${orgId}` },
    { action: "edit", path: "organizations-contacts" },
    "remove",
    "export",
    "activate",
    "deactivate"
  ];

  if (!isSpe) {
    actionsButtons.splice(1, 0, { text: "Add Existing", action: "add", path: `/organizations-borrowers/addexistingcontact?orgId=${orgId}` });
  }

  return <LmsTable
    actionButtons={actionsButtons}
    onClickPath="organizations-contacts"
    apiSuffix={suffix}
    deleteSuffix="orgs/contact-orgs"
    activateSuffix="orgs/activate-contacts"
    deActivateSuffix="orgs/deactivate-contacts"
    columnLabelsDictionary={GlobalConstants.ContactsTableColumnLabelsDictionary}
  />;
};

const LmsOrganizationsBorrowersTable = () => {
  const actionButtons = [
    "export",
    { text: "Add Master", action: "add" },
    { text: "Add SPE", action: "add", path: "organizations-borrowers/addspe" },
    "copy",
    "remove"
  ];

  return <LmsTable
    actionButtons={actionButtons}
    apiSuffix="orgs/borrower-spes"
    deleteSuffix="orgs/spes-or-orgs"
    hideProps={["filterLoanOfficerOrganizationContactCsvid"]}
    columnClickOptions={{
      organization: { path: "organizations-borrowers", idProp: "organizationId" },
      specompanyName: { path: "organizations-borrowers/editspe", idProp: "specialPurposeEntityId", params: ["organizationId"] }
    }}
    useGlobalDictionary
  />;
};

const LmsOrganizationsBorrowersActions = ({ ...props }: IDefaultPagesProps) => {
  const { params = {}, history } = props;
  const allowedActions = ["add", "edit", "copy", "addspe", "editspe"];
  const allowedSubpages = [
    "addnewcontact",
    "addexistingcontact",
    "addinsurance",
    "editinsurance",
    "addcomment",
    "viewcomment",
    "addstatetax",
    "editstatetax",
    "addscheduleofreo",
    "editscheduleofreo",
    "viewscheduleofreo",
    "addassestliability",
    "editassestliability",
    "viewassetsliabilities",
    "editspe",
    "viewadditionalinfo",
    "viewspeadditionalinfo"
  ] as any[];
  const { isAdd, isEdit, isCopy, actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const orgId = query.get("orgId") || id;
  const borrowerOrganizationId = query.get("organizationId") || query.get("borrowerOrganizationId") || "";
  const isAddSPE = actionLower === "addspe";
  const isEditSPE = actionLower === "editspe" && orgId;
  const isSpe = query.get("isSpe");
  const path = "organizations-borrowers";
  const editReoPath = "organizations-borrowers/editscheduleofreo/{id}?isSpe=true";

  if (!isActionAllowed) {
    history.goBack();
  }

  const tabPages = {
    Master: <EditBorrowerOrganization orgId={orgId} />,
    SPE: <SPETable orgId={orgId} />,
    Contacts: <ContactsTable suffix={`orgs/contacts/${orgId}`} orgId={orgId} />,
    Comments: <CommentsTable suffix={`orgs/comments/${orgId}`} path={path} orgId={orgId} />,
    Banks: <BanksTable suffix={`orgs/bank-infolist/${orgId}`} />,
    Insurance: <InsuranceTable suffix={`orgs/insurances/${orgId}`} path={path} orgId={orgId} />,
    "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} path={path} orgId={orgId} />,
    "Schedule of REO": <ScheduleOfReoTable suffix={`orgs/reo-schedules/${orgId}`} path={path} orgId={orgId} />,
    "Assets / Liabilities": <AssetsLiabilitiesTable suffix={`orgs/org-al-schedules/${orgId}`} orgId={orgId} />,
  };

  return <React.Fragment>
    {isMainPage && <LmsOrganizationsBorrowersTable />}
    {isEdit && <React.Fragment>
      <LmsTabPages tabPages={tabPages} />
    </React.Fragment>}
    {isAdd && <AddCopyBorrowerOrganization orgId={orgId} />}
    {isCopy && <AddCopyBorrowerOrganization isCopy={true} orgId={orgId} />}
    {isAddSPE && <AddSPEBorrowerOrganization orgId={orgId} />}
    {isEditSPE && <EditSPEBorrowerOrganization orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />}
    {isSubpageAllowed && <React.Fragment>
      {actionLower === "addexistingcontact" && <ExistingContactTable id={orgId} />}
      {actionLower === "addinsurance" && <AddInsuranceForm orgId={orgId} isBorrowerForm borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "editinsurance" && <EditInsuranceForm id={id} isBorrowerForm borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "addcomment" && <AddCommentForm orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "viewcomment" && <ViewCommentForm id={id} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "addstatetax" && <AddStateTaxForm orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "editstatetax" && <EditStateTaxForm id={id} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "addscheduleofreo" && <AddScheduleOfReoForm orgId={orgId} isSpe={isSpe === "true"} />}
      {actionLower === "editscheduleofreo" && <EditScheduleOfReoForm id={id} isSpe={isSpe === "true"} />}
      {actionLower === "viewscheduleofreo" && (
        <ScheduleOfReoTable suffix={`orgs/spe-reo-schedules/${orgId}`} path={path} orgId={orgId} isSpe={isSpe === "true"} borrowerOrganizationId={borrowerOrganizationId} editPath={editReoPath} />
      )}
      {actionLower === "addassestliability" && <AddAssetLiabilityForm orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "editassestliability" && <EditAssetLiabilityForm id={id} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "viewassetsliabilities" && <AssetsLiabilitiesTable suffix={`orgs/spe-al-schedules/${orgId}`} orgId={orgId} borrowerOrganizationId={borrowerOrganizationId} />}
      {actionLower === "viewadditionalinfo" && <ViewAdditionalBorrowerInfoForm id={id} orgId={orgId} />}
      {actionLower === "viewspeadditionalinfo" && (
        <ViewAdditionalBorrowerInfoForm orgId={orgId} isSpe={true} borrowerOrganizationId={borrowerOrganizationId} />
      )}
    </React.Fragment>}
  </React.Fragment>;
};

export default LmsOrganizationsBorrowersActions;